import clsx from 'clsx'
import { ReactNode, useRef } from 'react'
import { useFileDrop } from './hooks/useFileDrop'
import { RadialProgressBar } from './RadialProgressBar'

type Props = {
  onDrop: (dt: DataTransfer) => void
  children?: ReactNode
  caption?: string
  className?: string
}
export function MiniDocumentDropZone({ children, onDrop, caption, className }: Props) {
  const { isDragging, dropHandlers, onInputChange } = useFileDrop(onDrop)

  const fileInputRef = useRef<HTMLInputElement>(null)

  return (
    <div
      {...dropHandlers}
      onClick={() => {
        fileInputRef.current?.click()
      }}
      className={clsx(
        'flex cursor-pointer flex-col items-center rounded-md border-2 border-dashed border-gray-300 bg-stone-100 px-6 py-6 hover:border-blue-300 hover:bg-blue-100',
        isDragging ? 'border-blue-300 bg-blue-100' : null,
        className
      )}
    >
      <input type="file" hidden multiple onChange={onInputChange} ref={fileInputRef} />

      <CloudIcon className="size-6 text-stone-500 group-hover:text-blue-500" />

      <div className="mt-2 text-sm font-medium text-gray-700">
        {children ?? (
          <>
            {'Drag & Drop Files Here'} <a className="font-bold text-blue-500">or browse</a>
          </>
        )}
      </div>
      {caption ? <div className="mt-0.5 text-xs text-stone-600">{caption}</div> : null}
    </div>
  )
}

type DocumentDropZoneProps = {
  onDrop: (dt: DataTransfer) => void
  children: ReactNode
  className?: string
}
export function DocumentDropZone({ children, onDrop, className }: DocumentDropZoneProps) {
  const { isDragging, dropHandlers, onInputChange } = useFileDrop(onDrop)

  const fileInputRef = useRef<HTMLInputElement>(null)

  return (
    <div
      {...dropHandlers}
      onClick={() => {
        fileInputRef.current?.click()
      }}
      className={clsx(
        'group flex h-60 cursor-pointer flex-row rounded-md border border-dashed border-stone-300 bg-white px-6 py-6 hover:border-blue-300 hover:bg-blue-100',
        isDragging ? 'border-blue-300 bg-blue-100' : null,
        className
      )}
    >
      <input type="file" hidden multiple onChange={onInputChange} ref={fileInputRef} />

      {children}
    </div>
  )
}

export function DropProgress({ progress, caption }: { progress: number; caption?: string }) {
  return (
    <div className="flex grow flex-col items-center justify-center">
      <RadialProgressBar size={60} percentage={progress} />
      {caption ? <div className="mt-2 text-sm text-stone-600">{caption}</div> : null}
    </div>
  )
}

export function DropHere({ children, caption }: { children?: ReactNode; caption?: string }) {
  return (
    <div className="flex grow flex-col items-center justify-center">
      <CloudIcon className="mb-4 h-10 w-10 text-stone-500 group-hover:text-blue-500" />
      <div className="text-base font-medium text-gray-700">
        {children ?? (
          <>
            {`Drag & Drop Files Here`} <a className="font-bold text-blue-500">or browse</a>
          </>
        )}
      </div>
      {caption ? <div className="mt-2 text-sm text-stone-600">{caption}</div> : null}
    </div>
  )
}

function CloudIcon({ className }: { className: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M23.25 12C23.2547 13.7861 22.6752 15.5248 21.6 16.9509C21.5408 17.0297 21.4666 17.0961 21.3817 17.1462C21.2969 17.1964 21.203 17.2293 21.1054 17.2431C21.0078 17.257 20.9084 17.2514 20.813 17.2269C20.7175 17.2023 20.6278 17.1592 20.549 17.1C20.4702 17.0408 20.4039 16.9666 20.3538 16.8818C20.3036 16.7969 20.2707 16.703 20.2569 16.6054C20.243 16.5078 20.2485 16.4085 20.2731 16.313C20.2976 16.2175 20.3408 16.1279 20.4 16.0491C21.2801 14.883 21.7543 13.4609 21.75 12C21.75 10.2098 21.0388 8.4929 19.7729 7.22703C18.5071 5.96115 16.7902 5.25 15 5.25C13.2098 5.25 11.4929 5.96115 10.227 7.22703C8.96113 8.4929 8.24998 10.2098 8.24998 12C8.24998 12.1989 8.17096 12.3897 8.0303 12.5303C7.88965 12.671 7.69889 12.75 7.49998 12.75C7.30106 12.75 7.1103 12.671 6.96965 12.5303C6.82899 12.3897 6.74998 12.1989 6.74998 12C6.74961 11.2431 6.85339 10.4898 7.05841 9.76125C6.95622 9.75 6.8531 9.75 6.74998 9.75C5.5565 9.75 4.41191 10.2241 3.56799 11.068C2.72408 11.9119 2.24997 13.0565 2.24997 14.25C2.24997 15.4435 2.72408 16.5881 3.56799 17.432C4.41191 18.2759 5.5565 18.75 6.74998 18.75H8.99998C9.19889 18.75 9.38965 18.829 9.5303 18.9697C9.67096 19.1103 9.74998 19.3011 9.74998 19.5C9.74998 19.6989 9.67096 19.8897 9.5303 20.0303C9.38965 20.171 9.19889 20.25 8.99998 20.25H6.74998C5.92514 20.2502 5.10912 20.0803 4.35289 19.751C3.59666 19.4216 2.91646 18.9399 2.35476 18.3359C1.79307 17.7318 1.36195 17.0185 1.08833 16.2403C0.814718 15.4622 0.70448 14.636 0.764505 13.8134C0.82453 12.9907 1.05353 12.1893 1.4372 11.4591C1.82087 10.7289 2.35097 10.0857 2.9944 9.56961C3.63782 9.05351 4.38075 8.67562 5.17678 8.45954C5.97281 8.24346 6.80484 8.19383 7.62091 8.31375C8.45191 6.65171 9.81967 5.31888 11.5027 4.53115C13.1856 3.74342 15.0853 3.54693 16.8938 3.9735C18.7024 4.40007 20.314 5.42472 21.4676 6.88148C22.6212 8.33824 23.2493 10.1418 23.25 12ZM14.7806 11.4694C14.7109 11.3996 14.6282 11.3443 14.5372 11.3066C14.4461 11.2688 14.3485 11.2494 14.25 11.2494C14.1514 11.2494 14.0538 11.2688 13.9628 11.3066C13.8717 11.3443 13.789 11.3996 13.7194 11.4694L10.7193 14.4694C10.6497 14.5391 10.5944 14.6218 10.5567 14.7128C10.519 14.8039 10.4996 14.9015 10.4996 15C10.4996 15.0985 10.519 15.1961 10.5567 15.2872C10.5944 15.3782 10.6497 15.4609 10.7193 15.5306C10.8601 15.6714 11.051 15.7504 11.25 15.7504C11.3485 15.7504 11.4461 15.731 11.5371 15.6933C11.6282 15.6556 11.7109 15.6003 11.7806 15.5306L13.5 13.8103V19.5C13.5 19.6989 13.579 19.8897 13.7196 20.0303C13.8603 20.171 14.0511 20.25 14.25 20.25C14.4489 20.25 14.6397 20.171 14.7803 20.0303C14.921 19.8897 15 19.6989 15 19.5V13.8103L16.7194 15.5306C16.789 15.6003 16.8718 15.6556 16.9628 15.6933C17.0538 15.731 17.1514 15.7504 17.25 15.7504C17.3485 15.7504 17.4461 15.731 17.5371 15.6933C17.6282 15.6556 17.7109 15.6003 17.7806 15.5306C17.8503 15.4609 17.9056 15.3782 17.9433 15.2872C17.981 15.1961 18.0004 15.0985 18.0004 15C18.0004 14.9015 17.981 14.8039 17.9433 14.7128C17.9056 14.6218 17.8503 14.5391 17.7806 14.4694L14.7806 11.4694Z"
        fill="currentColor"
      />
    </svg>
  )
}
